import SEO from '../components/SEO';
import React from 'react';
import { motion } from 'framer-motion';
import { Download, Play, CheckCircle2, Users, Clock, Shield, Zap, HeadphonesIcon, Award, Target, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductCTA from '../components/ProductCTA';

const SupportPage = () => {
  const stats = [
    { value: "24x7", label: "Availability" },
    { value: "<2h", label: "Response Time" },
    { value: "4.9/5", label: "Satisfaction" },
    { value: "Always", label: "Here for You" }
  ];

  const support = [
    { icon: HeadphonesIcon, title: "Expert Support Team", strapline: "Real people, real expertise", desc: "Access our team of certified SAP Business One experts who know your system inside out. No outsourced call centers - just experienced professionals who care." },
    { icon: Clock, title: "Rapid Response", strapline: "We're here when you need us", desc: "Average response time under 2 hours for critical issues. We understand your business can't wait - neither do we." },
    { icon: Shield, title: "Proactive Monitoring", strapline: "Problems solved before you notice", desc: "24x7 system monitoring identifies and resolves potential issues before they impact your business operations." },
    { icon: Zap, title: "Regular Health Checks", strapline: "Continuous optimisation", desc: "Quarterly reviews ensure your system is optimised, users are productive, and you're getting maximum value from SAP Business One." },
    { icon: MessageCircle, title: "Dedicated Account Manager", strapline: "Your personal advocate", desc: "A dedicated contact who knows your business, your team, and your goals. Someone who's always looking out for your success." },
    { icon: Award, title: "Knowledge Transfer", strapline: "Build internal capability", desc: "We don't create dependency - we empower your team with knowledge and best practices so you can be self-sufficient." }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="SAP Business One Support Services | Proactive Monitoring & Expert Help"
        description="Dedicated SAP B1 support from UK-based experts. Proactive monitoring, fast response times, continuous optimization. We will never leave you."
        keywords="SAP B1 support, ERP support services, SAP Business One help, managed ERP support UK, SAP maintenance"
        canonical="https://www.ascarii.com/services/support"
      />

      <section className="relative min-h-[700px] flex items-center overflow-hidden bg-gradient-to-br from-green-700 via-emerald-800 to-green-900">
        <div className="absolute inset-0 z-0">
          <img src="https://images.pexels.com/photos/3823488/pexels-photo-3823488.jpeg?w=1920&auto=compress" alt="Support" className="w-full h-full object-cover opacity-30" loading="eager" />
        </div>
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-sm font-semibold mb-6 text-white">Ongoing Support</div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight text-white">We'll Never Leave You</h1>
              <p className="text-xl md:text-2xl text-green-100 mb-8 leading-relaxed">Your success is our success. Expert support available whenever you need it, proactive monitoring keeping you safe, and a dedicated team who genuinely cares about your business.</p>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-green-600 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Support That Truly Cares</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">You'll never be alone on your ERP journey - we're always here</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {support.map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: idx * 0.05 }} viewport={{ once: true, amount: 0.2 }} className="group p-8 bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 hover:border-green-300 hover:shadow-lg transition-all">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 flex items-center justify-center flex-shrink-0 group-hover:from-green-500 group-hover:to-emerald-500 transition-all">
                    <item.icon className="w-8 h-8 text-green-600 group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-green-600 transition-colors">{item.title}</h3>
                    <p className="text-sm text-green-600 font-medium mb-2">{item.strapline}</p>
                    <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-12 border-2 border-green-200">
              <div className="text-center">
                <div className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-6">Customer Feedback</div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Natgraph</h2>
                <p className="text-lg text-gray-700 italic mb-4">
                  Ascarii's consultants have a really good understanding of people, as well as systems, and made our team feel comfortable with what they're doing and how SAP Business One fits into our workplace environment.
                </p>
                <p className="text-sm text-gray-600">- Manufacturing Director, Natgraph</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Matching SAP B1 Showcase */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-br from-green-600 via-emerald-700 to-green-600">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Experience True Partnership Support</h2>
            <p className="text-xl md:text-2xl text-green-100 mb-12 leading-relaxed">
              Join businesses who never worry about their SAP Business One system. Let's discuss your support needs.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <a href="/contact" className="inline-flex items-center justify-center rounded-xl bg-white text-green-600 px-10 py-5 text-xl font-bold hover:bg-green-50 shadow-2xl transition-all hover:scale-105">
                Get in Touch Today
              </a>
              <a href="/experience" className="inline-flex items-center justify-center rounded-xl border-3 border-white text-white px-10 py-5 text-xl font-bold hover:bg-white/10 shadow-2xl transition-all hover:scale-105">
                Discover The Ascarii Experience
              </a>
            </div>
            <div className="mt-12 flex flex-wrap justify-center gap-8 text-white">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-green-300" />
                <span className="text-lg">24x7 Monitoring</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-green-300" />
                <span className="text-lg">Under 2h Response</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-6 h-6 text-green-300" />
                <span className="text-lg">4.9/5 Rated Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupportPage;
